import { uniform } from './uniform.ts';
import { shapeStructureDef } from './shapes.ts';
import { hoursMs, minutesMs } from './units.ts';

export interface Resources {
  wood: number;
  stone: number;
  crystal: number;
  dust: number;
  /** Era II on: only the iron mines give it (material 3, level-4 towers). */
  iron: number;
}

export const RESOURCE_KEYS = ['wood', 'stone', 'crystal', 'dust', 'iron'] as const;
export type ResourceKey = (typeof RESOURCE_KEYS)[number];

export const res = (r: Partial<Resources>): Resources => ({
  wood: r.wood ?? 0,
  stone: r.stone ?? 0,
  crystal: r.crystal ?? 0,
  dust: r.dust ?? 0,
  iron: r.iron ?? 0,
});

/** A structure's level (4 needs era II and an artificer) and material (3 is iron, era II). */
export type StructLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type Material = 1 | 2 | 3;

export type StructureType =
  | 'ballista'
  | 'catapult'
  | 'arcane'
  | 'belfry'
  | 'beacon'
  | 'wall'
  | 'gate'
  | 'spikes'
  | 'oil'
  | 'forge'
  | 'barracks'
  | 'temple'
  /** Era II: the artificer's workshop (one per bastion) and the clockwork turrets it makes. */
  | 'workshop'
  | 'clockwork'
  /** Era II: the alchemist's laboratory (one per bastion): stronger enchantments and elixirs. */
  | 'lab'
  /** Era II: the druid's grove (one per bastion): wood every day, roots that slow nightmares passing by. */
  | 'grove'
  /** Era III: the barbarian's clan hall (one per bastion): trophies of fallen warlords, war drums. */
  | 'hall'
  /** Era IV: the ranger's watchtower (one per bastion): towers nearby reach farther, it shows what hides and what digs. */
  | 'watchtower'
  /** Era IV: the cleric's sanctum (one per bastion): it mends what stands near it after every wave and blesses the heroes by it. */
  | 'sanctum'
  /** Era IV: the trebuchet, a tower of very long reach that hurls great stones at the ground nightmares, hardest on the big ones. */
  | 'trebuchet'
  /** Terraced land (docs/CONSTRUCCION.md §5): the gathering post golems unload at, out by the woods and the rock. */
  | 'depot'
  /** Terraced land: the golem works, where the group makes more golems (one more per level). */
  | 'golemworks'
  /** Terraced land, era II: the foundry, where golems bring iron out of the rock it touches. */
  | 'foundry'
  /** Terraced land, era III: the arsenal (3 cells): ballistas and catapults touching it fire faster. */
  | 'arsenal'
  /** Terraced land, era IV: the lightning rod: a storm's chain that would leap near it ends in it. */
  | 'rod'
  /** Terraced land, era IV: a gate tower, built beside a gate: it shoots like a light ballista and breaks apart from it. */
  | 'gatetower'
  /** Terraced land, era V: the Titan's Eye (7 cells): charged by what dies near it, it fires by itself at the biggest in reach. */
  | 'titanEye'
  /** Terraced land, a saber (docs/SABERES.md §4.1): the market of Musgoviejo (3 cells), one trade a day. */
  | 'market'
  /** Saberes (docs/SABERES.md §4.1): the boulder (a one-shot trap rolling downhill), the kennel (3 cells, mastiffs), the crane (faster jobs, one golem kept), the sieve (3 cells, dust from what falls near). */
  | 'boulder'
  | 'kennel'
  | 'crane'
  | 'sieve'
  /** A run of the ring wall around the bastion: stands from the start, can be broken and repaired, never built. */
  | 'rampart';

export type StructureCategory = 'tower' | 'defense' | 'trap' | 'support';

export interface StructureDef {
  type: StructureType;
  category: StructureCategory;
  /** Base HP at level 1, material 1. Traps have no HP (0). */
  hp: number;
  /** Range in fixed hex units (0 if none). */
  range: number;
  /** By level (1 to 6; level 5 towers come with era IV, level 6 on terraced land in era V). */
  damage: [number, number, number, number, number, number];
  /** Splash radius (fixed), 0 = single target. */
  splash: number;
  /** Reload in ticks. */
  reload: number;
  targetsGround: boolean;
  targetsAir: boolean;
  ignoresArmor: boolean;
  /** Stun ticks per level (belfry). */
  stun?: [number, number, number, number, number, number];
  /** Fire-rate aura bonus in bp per level (beacon). */
  auraBp?: [number, number, number, number, number, number];
  /** Reveal radius for invisible enemies (beacon). */
  revealRange?: number;
  /** Trap charges and effect. */
  charges?: number;
  slowBp?: number;
  slowTicks?: number;
  /** Damage over time: damage applied every `dotEvery` ticks for `dotTicks`. */
  dotEvery?: number;
  dotTicks?: number;
  /** Blocks ground movement. */
  blocks: boolean;
  /** Projectile travel speed in fixed units per tick (0 = instant). */
  projectileSpeed: number;
  cost: Resources;
  /** Hero level required to unlock (MVP: everything level 1 except stone material). */
  unlockLevel: number;
  /** The era it's first built in (absent: from the start). */
  minEra?: number;
  /** Only one in the bastion. */
  unique?: boolean;
  /** Needs this standing in the bastion first. */
  needs?: StructureType;
}

const base = {
  range: 0,
  damage: [0, 0, 0, 0, 0, 0] as [number, number, number, number, number, number],
  splash: 0,
  reload: 0,
  targetsGround: false,
  targetsAir: false,
  ignoresArmor: false,
  blocks: true,
  projectileSpeed: 0,
  unlockLevel: 1,
};

export const STRUCTURES: Record<StructureType, StructureDef> = uniform({
  ballista: {
    ...base,
    type: 'ballista',
    category: 'tower',
    hp: 300,
    range: 5500,
    damage: [18, 26, 36, 48, 64, 83],
    reload: 12,
    targetsGround: true,
    targetsAir: true,
    projectileSpeed: 1400,
    cost: res({ wood: 40, stone: 20 }),
  },
  catapult: {
    ...base,
    type: 'catapult',
    category: 'tower',
    hp: 350,
    range: 6000,
    damage: [30, 44, 62, 84, 112, 146],
    splash: 1200,
    reload: 30,
    targetsGround: true,
    projectileSpeed: 400,
    cost: res({ wood: 60, stone: 40 }),
  },
  arcane: {
    ...base,
    type: 'arcane',
    category: 'tower',
    hp: 250,
    range: 4500,
    damage: [10, 15, 21, 28, 37, 48],
    reload: 6,
    targetsGround: true,
    targetsAir: true,
    ignoresArmor: true,
    cost: res({ stone: 30, crystal: 30 }),
  },
  belfry: {
    ...base,
    type: 'belfry',
    category: 'tower',
    hp: 400,
    range: 2500,
    damage: [5, 5, 5, 6, 7, 9],
    splash: 2500,
    reload: 72,
    targetsGround: true,
    stun: [18, 24, 30, 38, 46, 54],
    cost: res({ stone: 50, wood: 30 }),
  },
  beacon: {
    ...base,
    type: 'beacon',
    category: 'tower',
    hp: 300,
    range: 3000,
    auraBp: [1500, 2000, 2500, 3000, 3500, 4000],
    revealRange: 4000,
    cost: res({ stone: 40, crystal: 20 }),
  },
  wall: {
    ...base,
    type: 'wall',
    category: 'defense',
    hp: 600,
    cost: res({ stone: 30 }),
  },
  gate: {
    ...base,
    type: 'gate',
    category: 'defense',
    hp: 1200,
    cost: res({ stone: 60, wood: 40 }),
  },
  spikes: {
    ...base,
    type: 'spikes',
    category: 'trap',
    hp: 0,
    damage: [25, 35, 48, 62, 80, 104],
    charges: 6,
    slowBp: 4000,
    slowTicks: 24,
    targetsGround: true,
    blocks: false,
    cost: res({ wood: 25 }),
  },
  oil: {
    ...base,
    type: 'oil',
    category: 'trap',
    hp: 0,
    damage: [8, 11, 15, 19, 24, 31],
    charges: 4,
    dotEvery: 12,
    dotTicks: 60,
    targetsGround: true,
    blocks: false,
    cost: res({ wood: 20, crystal: 10 }),
  },
  forge: {
    ...base,
    type: 'forge',
    category: 'support',
    /** One is enough: only the best one ever counts. */
    unique: true,
    hp: 500,
    cost: res({ stone: 80, wood: 40 }),
  },
  barracks: {
    ...base,
    type: 'barracks',
    category: 'support',
    /** One is enough: only the best one ever counts. */
    unique: true,
    hp: 500,
    cost: res({ wood: 60, stone: 60 }),
  },
  temple: {
    ...base,
    type: 'temple',
    category: 'support',
    /** One is enough: only the best one ever counts. */
    unique: true,
    hp: 400,
    range: 2500,
    cost: res({ stone: 50, crystal: 30 }),
  },
  workshop: {
    ...base,
    type: 'workshop',
    category: 'support',
    hp: 600,
    cost: res({ wood: 120, stone: 100, crystal: 40 }),
    minEra: 2,
    unique: true,
  },
  clockwork: {
    ...base,
    type: 'clockwork',
    category: 'tower',
    hp: 280,
    range: 3500,
    damage: [14, 20, 28, 38, 50, 65],
    reload: 8,
    targetsGround: true,
    targetsAir: true,
    projectileSpeed: 1300,
    cost: res({ wood: 40, crystal: 30 }),
    minEra: 2,
    needs: 'workshop',
  },
  lab: {
    ...base,
    type: 'lab',
    category: 'support',
    hp: 500,
    cost: res({ stone: 100, crystal: 120 }),
    minEra: 2,
    unique: true,
  },
  grove: {
    ...base,
    type: 'grove',
    category: 'support',
    hp: 450,
    range: 3000,
    cost: res({ stone: 100, crystal: 60 }),
    minEra: 2,
    unique: true,
  },
  hall: {
    ...base,
    type: 'hall',
    category: 'support',
    hp: 650,
    cost: res({ wood: 160, stone: 140, iron: 40 }),
    minEra: 3,
    unique: true,
  },
  /**
   * Era IV. Its `range` is the reach of its aura (towers within it reach farther, see WATCHTOWER in eras.ts);
   * `revealRange` how far it shows the invisible and marks the buried.
   */
  watchtower: {
    ...base,
    type: 'watchtower',
    category: 'support',
    hp: 550,
    range: 3500,
    revealRange: 4500,
    cost: res({ wood: 140, stone: 160, crystal: 60 }),
    minEra: 4,
    unique: true,
  },
  /** Era IV. Its `range` is how far its mending and its blessing reach (SANCTUM in eras.ts). */
  sanctum: {
    ...base,
    type: 'sanctum',
    category: 'support',
    hp: 550,
    range: 3000,
    cost: res({ stone: 180, crystal: 140 }),
    minEra: 4,
    unique: true,
  },
  /**
   * Era IV: half again the catapult's reach, a little more than twice its reload, more than twice its stone and a
   * wider blast; ground only. Big nightmares take more (TREBUCHET in eras.ts). To be measured with balance:probe.
   */
  trebuchet: {
    ...base,
    type: 'trebuchet',
    category: 'tower',
    hp: 450,
    range: 9000,
    damage: [70, 100, 140, 190, 250, 325],
    splash: 1500,
    reload: 66,
    targetsGround: true,
    projectileSpeed: 600,
    cost: res({ wood: 160, stone: 120, iron: 20 }),
    minEra: 4,
  },
  depot: {
    ...base,
    type: 'depot',
    category: 'support',
    hp: 420,
    cost: res({ wood: 70, stone: 30 }),
  },
  golemworks: {
    ...base,
    type: 'golemworks',
    category: 'support',
    hp: 600,
    cost: res({ wood: 120, stone: 140, crystal: 40 }),
    unique: true,
  },
  foundry: {
    ...base,
    type: 'foundry',
    category: 'support',
    hp: 600,
    cost: res({ wood: 100, stone: 160 }),
    minEra: 2,
    unique: true,
  },
  arsenal: {
    ...base,
    type: 'arsenal',
    category: 'support',
    hp: 650,
    cost: res({ wood: 140, stone: 160, iron: 20 }),
    minEra: 3,
  },
  rod: {
    ...base,
    type: 'rod',
    category: 'support',
    hp: 300,
    /** How near a chain's next leap it has to stand to take it. */
    range: 3000,
    blocks: true,
    cost: res({ stone: 40, iron: 30 }),
    minEra: 4,
  },
  gatetower: {
    ...base,
    type: 'gatetower',
    category: 'tower',
    hp: 520,
    range: 4500,
    damage: [14, 20, 28, 38, 50, 65],
    reload: 14,
    targetsGround: true,
    targetsAir: true,
    projectileSpeed: 1400,
    cost: res({ wood: 40, stone: 60 }),
    minEra: 4,
  },
  titanEye: {
    ...base,
    type: 'titanEye',
    category: 'support',
    hp: 3000,
    range: 7000,
    cost: res({ stone: 800, crystal: 400, iron: 200 }),
    minEra: 5,
    unique: true,
  },
  market: {
    ...base,
    type: 'market',
    category: 'support',
    hp: 520,
    cost: res({ wood: 140, stone: 90 }),
    unique: true,
  },
  boulder: {
    ...base,
    type: 'boulder',
    category: 'trap',
    hp: 0,
    // Set with tools/balance/src/saberes-probe.ts against three spikes for about the same spend (2026-11-04).
    damage: [110, 150, 205, 270, 350, 455],
    charges: 1,
    targetsGround: true,
    blocks: false,
    cost: res({ wood: 40, stone: 90 }),
  },
  kennel: {
    ...base,
    type: 'kennel',
    category: 'support',
    hp: 480,
    cost: res({ wood: 150, stone: 60 }),
    unique: true,
  },
  crane: {
    ...base,
    type: 'crane',
    category: 'support',
    hp: 300,
    cost: res({ wood: 120, stone: 40 }),
    unique: true,
  },
  sieve: {
    ...base,
    type: 'sieve',
    category: 'support',
    hp: 380,
    cost: res({ wood: 110, stone: 50 }),
    unique: true,
  },
  rampart: {
    ...base,
    type: 'rampart',
    category: 'defense',
    hp: 1500,
    cost: res({ stone: 40 }),
  },
}, shapeStructureDef);

export const STRUCTURE_TYPES = Object.keys(STRUCTURES) as StructureType[];
/** Only on terraced land (docs/CONSTRUCCION.md). */
export const LAND_ONLY_TYPES: StructureType[] = ['depot', 'golemworks', 'foundry', 'arsenal', 'rod', 'gatetower', 'titanEye', 'market', 'boulder', 'kennel', 'crane', 'sieve'];
export const BUILDABLE_TYPES: StructureType[] = STRUCTURE_TYPES.filter((t) => t !== 'gate' && t !== 'rampart' && !LAND_ONLY_TYPES.includes(t));

/** Cost multiplier per level in bp: L1 ×1, L2 ×1.8, L3 ×3.2, L4 ×5 (towers from era II, the forge from era III, plus iron), L5 ×7.5 (era IV). */
export const LEVEL_COST_BP: [number, number, number, number, number, number] = [10000, 18000, 32000, 50000, 75000, 100000];
/** HP multiplier per level in bp (not in spec: +25 % per level). */
export const LEVEL_HP_BP: [number, number, number, number, number, number] = [10000, 12500, 15000, 17500, 20000, 22500];
/** Iron a level-4 structure asks on top of its cost (a level 5 one, `LEVEL5_IRON`). */
export const LEVEL4_IRON = 40;
export const LEVEL5_IRON = 90;
/** Iron the level 6 forge (era V) asks on top of its cost. */
export const LEVEL6_IRON = 150;
/** Material HP multiplier: 1 wood & adobe ×1.0, 2 stone ×1.4, 3 iron ×1.8. */
export const MATERIAL_HP_BP: Record<Material, number> = { 1: 10000, 2: 14000, 3: 18000 };
/** Hero level that unlocks each material (iron also needs era II). */
export const MATERIAL_UNLOCK_LEVEL: Record<Material, number> = { 1: 1, 2: 6, 3: 7 };
/** Iron per level of the structure to clad it in iron, on top of the material cost. */
export const MATERIAL3_IRON_PER_LEVEL = 20;
/** Iron-clad structures take this share of the damage nightmares deal them. */
export const IRON_DAMAGE_TAKEN_BP = 8000;
/** Level-3 structures need an artificer. */
export const LEVEL3_REQUIRES = 'artificer';

/** Real-time build durations per level. */
export const BUILD_TIME_MS: [number, number, number, number, number, number] = [minutesMs(20), hoursMs(2), hoursMs(6), hoursMs(12), hoursMs(24), hoursMs(36)];
/** Material upgrade takes this share of the level's build time. */
export const MATERIAL_UPGRADE_TIME_BP = 5000;
/** One minute of hero work equals this many minutes of construction. */
export const WORK_MULTIPLIER = 5;
/** Golems: 3 + floor(players / 2). */
export const golemCount = (players: number): number => 3 + Math.floor(players / 2);
/** Repair: 30 % of base cost per 100 % of missing HP, prorated. */
export const REPAIR_COST_BP = 3000;
/** Golem repair speed between waves: HP per minute of real time (unused: repairs are ordered jobs now). */
export const GOLEM_REPAIR_HP_PER_MIN = 20;
/** A full repair takes this long for one golem (prorated to the damage, at least REPAIR_MIN_MS); an artificer's takes half. */
export const REPAIR_TIME_MS = minutesMs(20);
export const REPAIR_MIN_MS = minutesMs(2);
/** A job called off within this much work gives back all it was paid (a plan drawn by mistake, taken back at once). */
export const CANCEL_GRACE_MS = minutesMs(2);
/** Demolition can be undone during this window. */
export const DEMOLISH_UNDO_MS = hoursMs(24);
/** Demolition refunds this share of what was paid. */
export const DEMOLISH_REFUND_BP = 5000;
